import { useState } from 'react';
import { IonHeader, IonPage } from '@ionic/react';
import {
  APP_HEADER_CLASS,
  APP_HEADER_INNER_CLASS,
  ICON_BUTTON_CLASS,
} from '@mobile/ui';
import { useFilePicker } from '@shared/hooks';
import {
  ImageIcon,
  InstallPrompt,
  ProgressBar,
  SettingsIcon,
} from '@shared/ui';
import { cn, haptic } from '@shared/utils';
import { usePhotoConverter } from '@square-photo/hooks/use-photo-converter';
import {
  IdleScreen,
  SelectingScreen,
  ProcessingScreen,
  DoneScreen,
} from '@square-photo/components/screens';
import { SettingsSheet } from '@square-photo/components/settings-sheet';
import { MAX_PHOTOS } from '@square-photo/stores/photo-converter';
import type { AppPhase } from '@square-photo/types';
import { isHeicFile } from '@square-photo/utils';

/**
 * 應用外殼：整合 hooks，依 phase 切換對應的畫面。
 *
 * 流程：idle → selecting → processing → done →（reset）→ idle
 *
 * **外殼與畫面的分工：**`IonHeader` 由這裡統一持有，各 phase 只提供
 * `IonContent`（與需要時的 `IonFooter`）。標題會隨 phase 換句話，但**它始終是同一條
 * 工具列**——設定入口不會在流程中途換位置或消失，進度也就不需要在每個畫面各做一份。
 *
 * 各畫面回傳的是 fragment 而非包一層 div：`IonPage` 靠直屬子節點的元素型別決定
 * 版面（header／content／footer 三段式），中間多包一層會讓 content 失去捲動高度。
 */
export function PhotoApp() {
  const [state, actions] = usePhotoConverter();
  const { open, inputRef, handleChange } = useFilePicker({
    onFilesSelected: actions.addPhotos,
  });
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const hasHeicPhotos = state.photos.some((photo) => isHeicFile(photo.file));
  /* 這兩個 phase 底部有 IonFooter，安裝提示要讓開 */
  const hasFooter = state.phase === 'selecting' || state.phase === 'done';
  const total = state.photos.length;
  const progressValue =
    total > 0 ? Math.round((state.currentProcessingIndex / total) * 100) : 0;

  return (
    <IonPage>
      {/*
        `ion-no-border` 關掉 iOS mode 預設的下緣線——下緣線由 APP_HEADER_CLASS 畫，
        位置在整條橫幅（含進度與安裝引導）的最下方。

        **橫幅本體是自訂的 `<header>` 而非 `IonToolbar`**，與 diet 相同：iOS mode 的
        `ion-title` 置中、MD mode 靠左，用它會讓同一份程式碼在兩個平台長不一樣，
        而本專案刻意不鎖 mode。外觀（底色、模糊、下緣線、safe-area、對齊線）走
        @mobile/ui 的共用約定，這裡只決定裡面放什麼。
      */}
      <IonHeader className="ion-no-border">
        <header className={APP_HEADER_CLASS}>
          <div className={APP_HEADER_INNER_CLASS}>
            {/* 整頁唯一的標題，同時顯示流程狀態（已選幾張、轉換到第幾張） */}
            <h1 className="font-display text-base tracking-tight">
              {headerTitle(state.phase, state.currentProcessingIndex, total)}
            </h1>

            {/*
              設定只有在流程沒跑起來時才進得去：轉換途中離開畫面會讓使用者看不到
              進度，而那是唯一無法重來的一段（照片已在處理中）。
            */}
            <button
              type="button"
              onClick={() => {
                haptic('tap');
                setIsSettingsOpen(true);
              }}
              disabled={state.phase === 'processing'}
              /* 圖示鍵的內距讓它視覺上內縮，-mr 補回來對齊內容右緣 */
              className={cn(ICON_BUTTON_CLASS, '-mr-2.5')}
              aria-label="設定"
            >
              <SettingsIcon size={18} />
            </button>
          </div>

          {/*
            進度放橫幅內而非內容頂端：內容是可捲動的縮圖網格，捲下去之後就看不到
            自己還剩幾張。橫幅不在捲動容器內，進度因此一直在視線裡。
          */}
          {state.phase === 'processing' && (
            <div className="mx-auto flex max-w-5xl flex-col gap-2 px-4 pb-3 lg:px-6">
              <ProgressBar
                value={progressValue}
                aria-label={`轉換進度 ${progressValue}%`}
              />
              {hasHeicPhotos && (
                <p className="text-xs text-muted-foreground">
                  HEIC 格式轉換較慢，請耐心等候
                </p>
              )}
            </div>
          )}
        </header>
      </IonHeader>

      {/*
        檔案輸入是隱藏的觸發器，不參與版面。放在 IonPage 內、header 之後——它不是
        header／content／footer 三段之一，Ionic 會忽略它。
      */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        onChange={handleChange}
        className="hidden"
        aria-hidden="true"
      />

      {state.phase === 'idle' && <IdleScreen onSelectPhotos={open} />}

      {/*
        安裝提示在每個 phase 都在——它問的是「要不要把這個工具留在手機上」，與
        使用者走到流程哪一步無關。

        有底部操作列的 phase（開始轉換／下載 ZIP）要把卡片抬到操作列上方：蓋住
        那些鍵等於為了推銷安裝擋掉使用者真正要按的東西。
      */}
      <InstallPrompt
        appName="方形照片"
        icon={<ImageIcon size={22} />}
        className={cn(
          hasFooter && 'bottom-[calc(5rem+env(safe-area-inset-bottom))]',
        )}
      />

      {state.phase === 'selecting' && (
        <SelectingScreen
          photos={state.photos}
          onAddMore={open}
          onRemovePhoto={actions.removePhoto}
          onStartConvert={actions.startConvert}
        />
      )}

      {state.phase === 'processing' && (
        <ProcessingScreen photos={state.photos} />
      )}

      {state.phase === 'done' && (
        <DoneScreen
          photos={state.photos}
          processedPhotos={state.processedPhotos}
          zipFilename={state.zipFilename}
          canShare={state.canSharePhotos}
          onShare={actions.sharePhotos}
          onDownloadZip={actions.downloadZip}
          onReset={actions.reset}
        />
      )}

      <SettingsSheet
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
    </IonPage>
  );
}

/**
 * 工具列標題。
 *
 * 每個 phase 換一句話，讓標題同時擔任狀態指示——選取中的張數與轉換進度都在這裡，
 * 內容區就不必再各放一條狀態列。
 */
function headerTitle(
  phase: AppPhase,
  currentIndex: number,
  total: number,
): string {
  switch (phase) {
    case 'selecting':
      return `已選 ${total} / ${MAX_PHOTOS} 張`;
    case 'processing':
      /* 索引是「正在處理第幾張」，對使用者要顯示成 1 起算 */
      return `轉換中 ${Math.min(currentIndex + 1, total)} / ${total} 張`;
    case 'done':
      return '轉換完成';
    default:
      return '方形照片';
  }
}
